import React from 'react';
import { ExternalLink, RefreshCw, Pencil } from 'lucide-react';
import { Button } from '../../_shared/components';
import type { Campaign } from './types';
import { formatEventDate } from '../utils/dates';

interface CampaignInfoPanelProps {
  campaign: Campaign;
  onRefresh: () => void;
  onEdit?: () => void;
}

function formatDate(iso: string | null | undefined): string {
  return formatEventDate(iso) ?? '—';
}

/**
 * `budgeted_cost`, `pipeline_goal` and `arr_goal` are `NUMERIC(12,2)`, which
 * `prisma.$queryRaw` hands back as a Prisma `Decimal` — JSON-serialized as a
 * STRING, not a number. The old `number`-only signature was a type assertion
 * that never held at runtime, and `'15000.00'.toLocaleString()` is a no-op, so
 * these three fields rendered as `$15000.00` instead of `$15,000`. Parsing the
 * shared launcher schema (QA round 8, U2) widened the type to the truth and
 * surfaced it.
 */
function formatCurrency(val: number | string | null | undefined): string {
  if (val == null) return '—';
  const n = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(n)) return '—';
  return `$${n.toLocaleString()}`;
}

function formatNumber(val: number | null | undefined): string {
  if (val == null) return '—';
  return val.toLocaleString();
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="grid grid-cols-[140px_1fr] py-2 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 rounded-lg px-2 -mx-2 transition-colors">
      <span className="text-xs text-nightshade-500 dark:text-nightshade-400">{label}</span>
      <span className="text-xs text-nightshade-800 dark:text-nightshade-200 whitespace-pre-wrap break-words min-w-0">{value}</span>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 dark:text-nightshade-400 whitespace-nowrap">{title}</h3>
        <div className="h-px flex-1 bg-nightshade-200 dark:bg-nightshade-700" />
      </div>
      <div>{children}</div>
    </div>
  );
}

export function CampaignInfoPanel({ campaign, onRefresh, onEdit }: CampaignInfoPanelProps) {
  const sfdcUrl = `https://clay.lightning.force.com/lightning/r/Campaign/${campaign.id}/view`;
  const location = [campaign.city, campaign.state].filter(Boolean).join(', ') || null;

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex items-center gap-3">
        <a
          href={sfdcUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-blueberry-500 hover:text-blueberry-600 dark:text-blueberry-400 dark:hover:text-blueberry-300 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          View in Salesforce
        </a>
        <Button variant="ghost" size="sm" onClick={onRefresh} className="flex items-center gap-1.5 !rounded-full !h-8">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
        {onEdit && (
          <Button variant="ghost" size="sm" onClick={onEdit} className="flex items-center gap-1.5 !rounded-full !h-8">
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </Button>
        )}
      </div>

      {/* Event Details */}
      <Section title="Event Details">
        <Field label="Type" value={campaign.type || '—'} />
        <Field label="Subtype" value={campaign.subtype || '—'} />
        <Field label="Status" value={campaign.status || '—'} />
        <Field label="Start Date" value={formatDate(campaign.event_date ?? campaign.start_date)} />
        <Field label="End Date" value={formatDate(campaign.end_date)} />
        <Field label="Location" value={location ?? '—'} />
        <Field label="Capacity" value={formatNumber(campaign.capacity)} />
        <Field label="Budgeted Cost" value={formatCurrency(campaign.budgeted_cost)} />
        <Field label="Description" value={campaign.description || '—'} />
        {campaign.event_brief_url ? (
          <div className="grid grid-cols-[140px_1fr] py-2 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 rounded-lg px-2 -mx-2 transition-colors">
            <span className="text-xs text-nightshade-500 dark:text-nightshade-400">Event Brief</span>
            <a
              href={campaign.event_brief_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blueberry-500 hover:text-blueberry-600 dark:text-blueberry-400 underline break-all"
            >
              {campaign.event_brief_url}
            </a>
          </div>
        ) : (
          <Field label="Event Brief" value="—" />
        )}
        <Field label="Target Persona" value={campaign.target_persona ?? '—'} />
      </Section>

      {/* Goals */}
      <Section title="Goals">
        <Field label="Goal" value={campaign.goal || '—'} />
        <Field label="Registration Goal" value={formatNumber(campaign.registration_goal)} />
        <Field label="Pipeline Goal" value={formatCurrency(campaign.pipeline_goal)} />
        <Field label="ARR Goal" value={formatCurrency(campaign.arr_goal)} />
      </Section>

      {/* Partner */}
      <Section title="Partner">
        <Field label="Partner" value={campaign.partner ?? '—'} />
        <Field label="Activation Items" value={campaign.partner_activation_items ?? '—'} />
      </Section>

      {/* Invite Copy */}
      <Section title="Invite Copy">
        <Field label="Subject" value={campaign.invite_subject ?? '—'} />
        <Field label="Body" value={campaign.invite_body ?? '—'} />
      </Section>

      {/* Sync / Meta */}
      <Section title="Sync / Meta">
        <Field label="Members" value={formatNumber(campaign.member_count)} />
        <Field label="Last Synced" value={formatDate(campaign.last_synced_at)} />
        <Field label="Created" value={formatDate(campaign.created_at)} />
        <Field label="Updated" value={formatDate(campaign.updated_at)} />
      </Section>
    </div>
  );
}
